import React, {useState} from "react";
import {IconButton, InputAdornment, TextField, Tooltip} from "@mui/material";
import {ImageOutlined} from "@mui/icons-material";
import {createUseStyles} from "react-jss";
import {Camera} from "lucide-react";
import {useScrollyTellProps, useScrollytellTranslation,} from "../Admin/scrollytellContext";
import {ImageMenu} from "./ImageMenu";
import {ImageAltTextDialog} from "./ImageAltTextDialog";
import {getBackgroundImageString} from "../helpers/helpers";
import {ResponsiveImage} from "../image/ResponsiveImage";
import {AdminActions} from "../Block/AdminActions";

export const EditImageBlock = ({block, contrastApproved, onUpdate, isActiveBlock, children}) => {
    const t = useScrollytellTranslation();
    const {onMediaSelect} = useScrollyTellProps();
    const [altDialogOpen, setAltDialogOpen] = useState(false);

    const classes = useStyles({
        isActiveBlock,
        contrastApproved,
    });

    const updateProperty = (property, value) => onUpdate({
        ...block,
        [property]: value,
    });

    const setImage = () => {
        if (onMediaSelect) {
            onMediaSelect("image", (dmsId) => {
                updateProperty('imagePath', dmsId);
            });
        } else {
            console.warn("onMediaSelect is undefined");
            updateProperty("imagePath", "019EE6jCchh6X");
        }
    };

    const handleUpdateCaption = event => updateProperty("caption", event.target.value);

    return (
        <>
            {block.imagePath && (
                <ResponsiveImage
                    mediaId={block.imagePath}
                    alt={block.isDecorative ? "" : block.altText}
                    className={classes.img}
                />
            )}
            <label
                className={classes.imageUpload}
                style={{position: block.imagePath ? "absolute" : "initial"}}
                htmlFor="imageBlockUpload"
                onClick={() => setImage()}
            >
                {!block.imagePath && (
                    <div
                        role="button"
                        tabIndex={0}
                        aria-label={t("editImageBlock.clickToSelectImage", "Klikk for å velge bilde")}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setImage(); } }}
                        className={classes.imagePlaceholder}
                        style={{ backgroundImage: getBackgroundImageString(placeholderBackground, undefined, true) }}
                    >
                        <Tooltip title={t("editImageBlock.uploadImage", "Velg et bilde")}>
                            <IconButton aria-label={t("editImageBlock.uploadImage", "Velg et bilde")} component="span" sx={{ backgroundColor: "#FAFAFA" }}>
                                <ImageOutlined/>
                            </IconButton>
                        </Tooltip>
                        <span style={{ fontSize: "14px", fontWeight: 500, color: "#1E293B" }}>
                            {t("editImageBlock.clickToSelectImage", "Klikk for å velge bilde")}
                        </span>
                    </div>
                )}
            </label>
            {block.imagePath && block.caption !== undefined && (
                <div className={classes.imageBlockCaption}>
                    <TextField
                        className={classes.imageBlockCaptionText}
                        value={block.caption || ""}
                        onChange={handleUpdateCaption}
                        label="Caption"
                        InputProps={
                            block.showCaptionIcon
                                ? {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Camera className={classes.captionIcon}/>
                                        </InputAdornment>
                                    ),
                                }
                                : undefined
                        }
                    />
                </div>
            )}
            <AdminActions
                show={isActiveBlock}
            >
                {children}
                <ImageMenu
                    block={block}
                    onUpdateProperty={updateProperty}
                    handleSetImage={() => setImage()}
                    onOpenAltDialog={() => setAltDialogOpen(true)}
                />
            </AdminActions>
            <ImageAltTextDialog
                open={altDialogOpen}
                onClose={() => setAltDialogOpen(false)}
                block={block}
                onSave={({ isDecorative, altText }) => {
                    onUpdate({ ...block, isDecorative, altText });
                    setAltDialogOpen(false);
                }}
            />
        </>
    );
};

const useStyles = createUseStyles({
    img: {
        maxWidth: "100%"
    },
    imageUpload: {
        top: "2px",
        left: "calc(100% - 52px)",
        cursor: "pointer",
    },
    imagePlaceholder: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "12px",
        backgroundColor: "#A6A6A661",
        border: "2px dashed #4484F1",
        borderRadius: "4px",
        height: "500px",
        backgroundRepeat: "repeat",
        backgroundSize: "auto",
        outline: "none",
        "&:focus-visible": {
            outline: "2px solid #4484F1",
            outlineOffset: "2px",
        },
    },
    imageBlockEditIconButton: {
        height: "48px !important",
        width: "48px !important",
        minWidth: "48px !important",
        backgroundColor: "#FFFFFF !important",
        boxShadow: "0px 1px 2px rgba(12, 11, 15, 0.06), 0px 1px 3px rgba(12, 11, 15, 0.1) !important",
        border: "1px solid rgba(12, 11, 15, 0.05)",
        borderRadius: "4px !important",
        opacity: 0.8,
        cursor: "pointer",
    },
    imageBlockEditIconText: {
        fontSize: "14px",
        fontStyle: "italic",
    },
    imageBlockCaption: {
        padding: "16px",
    },
    imageBlockEditButton: {
        height: "48px",
    },
    imageBlockCaptionText: {
        width: "100%",
        "& input, & label": {
            color: ({contrastApproved}) =>
                contrastApproved ? "#FFFFFF" : "#0C0B0F",
        },
    },
    captionIcon: {
        width: "12px",
        margin: "0 14px",
        color: ({contrastApproved}) =>
            contrastApproved ? "#FFFFFF" : "#0C0B0F",
    },
});

const placeholderBackground = "013AjtNnCUXw";
